import { getCertificate, getAllCertificates, Certificate } from 'crypto-pro-js';
import { ICertificateInfo } from '../Types/Certificate';
import axios from 'axios';

//#region Константы.

const CADESCOM_CADES_BES = 1;
const CAPICOM_CURRENT_USER_STORE = 2;
const CAPICOM_MY_STORE = 'My';
const CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED = 2;
const CAPICOM_CERTIFICATE_FIND_SHA1_HASH = 0;
const CAPICOM_CERTIFICATE_FIND_ROOT_NAME = 3;
const CADESCOM_XML_SIGNATURE_TYPE_TEMPLATE = 2;
const CADESCOM_BASE64_TO_BINARY = 1;

const INFO_NAME = 0;
const INFO_COMPANY_NAME = 1;
const INFO_EMAIL = 2;
const INFO_CITY = 3;

const XmlDsigGost3410Url =
	'urn:ietf:params:xml:ns:cpxmlsec:algorithms:gostr34102001-gostr3411';
const XmlDsigGost3411Url =
	'urn:ietf:params:xml:ns:cpxmlsec:algorithms:gostr3411';

//#endregion

type IResult<T> =
	| { data: T; success: true; message: string }
	| { data?: undefined; success: false; message: string };

interface IBase64Cer {
	base64: string;
	subjectName: string;
}

class APICertificate {
	/**
	 * Метод получения списка сертификатов
	 * */
	static getCertList = async (): Promise<ICertificateInfo[]> => {
		const certList = await getAllCertificates(false);
		if (!certList.length) {
			console.warn('Не удалось получить список сертификатов.');
		}
		// Генерируем массив с промисами преобразующих сертификаты в объекты только с нужной нам инфой
		const promises = certList.map((c) => {
			return this.prepareCertInfo(c);
		});
		// Выполняем все промисы и возвращаем результаты
		return await Promise.all(promises);
	};

	static prepareCertInfo = async (
		oCertificate: Certificate,
	): Promise<ICertificateInfo> => {
		const subjectInfo = await oCertificate.getOwnerInfo();
		const issuerInfo = await oCertificate.getIssuerInfo();

		const isDateValid = new Date(oCertificate.validTo) > new Date();
		// При ошибке (недоступен OCSP/CRL или плагин) — не блокируем пользователя, считаем true
		const hasPrivateKey = !!(await oCertificate
			.getCadesProp('HasPrivateKey')
			.catch(() => {
				return true;
			}));
		const isTrustValid = await oCertificate.isValid().catch(() => {
			return true;
		});

		const certInfo: ICertificateInfo = {
			thumbprint: oCertificate.thumbprint,

			subjectName: subjectInfo[INFO_NAME]?.description,
			subjectCompanyName: subjectInfo[INFO_COMPANY_NAME]?.description,
			subjectEmail: subjectInfo[INFO_EMAIL]?.description,
			subjectCity: subjectInfo[INFO_CITY]?.description,

			issuerName: issuerInfo[INFO_NAME]?.description,
			issuerCompanyName: issuerInfo[INFO_COMPANY_NAME]?.description,
			issuerEmail: issuerInfo[INFO_EMAIL]?.description,
			issuerCity: issuerInfo[INFO_CITY]?.description,

			validToDate: new Date(oCertificate.validTo),
			isDateValid,
			hasPrivateKey,
			isValid: isDateValid && hasPrivateKey,
			isTrustValid,
		};

		return certInfo;
	};

	/** Проверка работы плагина Крипто-Про  */
	static validateCryptSystem = async () => {
		await getAllCertificates();
		return true;
	};

	/** Возвращает сертификат в формате Base64 и имя владельца сертификата  */
	static getCertInfoByThumbprint = async (
		thumbprint: string,
	): Promise<IBase64Cer> => {
		const cert = await getCertificate(thumbprint);
		return {
			base64: await cert.exportBase64(),
			subjectName: (await cert.getOwnerInfo())[INFO_NAME].description,
		};
	};

	/**получаем список отпечатков квалифицированных сертификатов (с root name 7710474375) */
	static getQualifiedThumbprints = async (): Promise<Set<string>> => {
		const result = new Set<string>();
		const rootName = '7710474375';
		try {
			if (window.cadesplugin?.CreateObjectAsync) {
				const oStore =
					await window.cadesplugin.CreateObjectAsync(
						'CAdESCOM.Store',
					);
				await oStore.Open(
					CAPICOM_CURRENT_USER_STORE,
					CAPICOM_MY_STORE,
					CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED,
				);
				const allCertificates = await oStore.Certificates;
				const filtered = await allCertificates.Find(
					CAPICOM_CERTIFICATE_FIND_ROOT_NAME,
					rootName,
				);
				const count = await filtered.Count;
				for (let i = 1; i <= count; i++) {
					const cert = await filtered.Item(i);
					const thumbprint = await cert.Thumbprint;
					if (thumbprint) {
						result.add(String(thumbprint));
					}
				}
				await oStore.Close();
			} else if (window.cadesplugin?.CreateObject) {
				const oStore = window.cadesplugin.CreateObject('CAPICOM.Store');
				oStore.Open(
					CAPICOM_CURRENT_USER_STORE,
					CAPICOM_MY_STORE,
					CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED,
				);
				const allCertificates = oStore.Certificates;
				const filtered = allCertificates.Find(
					CAPICOM_CERTIFICATE_FIND_ROOT_NAME,
					rootName,
				);
				const count = filtered.Count;
				for (let i = 1; i <= count; i++) {
					const cert = filtered.Item(i);
					const thumbprint = cert?.Thumbprint;
					if (thumbprint) {
						result.add(String(thumbprint));
					}
				}
				oStore.Close();
			}
		} catch {}
		return result;
	};

	static getHashAlgIdBySendSoap = async (certificate: string) => {
		const result = await axios.post(
			window.host + '/SendSoap/GetHashAlgId',
			{ certificate },
			{ withCredentials: true },
		);
		return result.data;
	};

	/** Метод подписи хэш */
	static async signHash(
		hash: string,
		hashAlgId: number,
		thumbprint: string,
		isRaw?: false,
	): Promise<IResult<string>>;
	static async signHash(
		hash: string,
		hashAlgId: number,
		thumbprint: string,
		isRaw: true,
	): Promise<IResult<{ sign: string; cert: string }>>;
	static async signHash(
		hash: string,
		hashAlgId: number,
		thumbprint: string,
		isRaw = false,
	): Promise<IResult<string> | IResult<{ sign: string; cert: string }>> {
		const certResult = await this._getCertificate(thumbprint);
		if (!certResult.success) {
			throw new Error(
				'Ошибка при получении сертификата. ' + certResult.message,
			);
		}

		const initResult = await this._initializeHashedData(hashAlgId, hash);
		if (!initResult.success) {
			throw new Error(
				'Ошибка при инициализации хэш. ' + initResult.message,
			);
		}

		if (isRaw) {
			const signedData = await this._createRawSignature(
				certResult.data!,
				initResult.data,
			);
			return {
				data: {
					sign: signedData.sign,
					cert: signedData.cert,
				},
				success: true,
				message: '',
			} as IResult<{ sign: string; cert: string }>;
		}

		const signResult = await this._createSignHash(
			certResult.data!,
			initResult.data,
		);
		if (!signResult.success) {
			throw new Error(
				'Ошибка при вычислении подписи. ' + signResult.message,
			);
		}
		return signResult as IResult<string>;
	}

	/** подписываем массив хешей и возвращаем массив подписей */
	static getSignListWithHash = async (
		sourceArray: string[],
		thumbprint: string,
		hashAlgId = 0,
	): Promise<string[]> => {
		const signArrayAs: string[] = [];
		const oCertificate = await this._getCertificate(thumbprint);
		if (!oCertificate.success) {
			throw new Error(
				'Ошибка при получении сертификата. ' + oCertificate.message,
			);
		}

		for (let i = 0; i < sourceArray.length; i++) {
			const oHashedData = await this._initializeHashedData(
				hashAlgId,
				sourceArray[i],
			);
			if (!oHashedData.success) {
				continue;
			}

			const sRawSignature = await this._createSignHash(
				oCertificate.data,
				oHashedData.data,
			);
			if (sRawSignature.success) {
				signArrayAs.push(sRawSignature.data);
			}
		}
		return signArrayAs;
	};

	/** пытаемся декодировать base64 строку, возвращаем исходную при ошибке */
	private static tryDecodeBase64 = (str: string): string => {
		try {
			return atob(str);
		} catch {
			return str;
		}
	};

	/** подписываем XML или хеш */
	static signXmlPlugin = async (thumbprint: string, digestXml: string) => {
		if (!digestXml) {
			throw new Error('В запросе на подписывание XML не указан контент');
		}

		let xmlSourceString = digestXml;
		if (!xmlSourceString.startsWith('<')) {
			const decoded = this.tryDecodeBase64(xmlSourceString);
			if (decoded.startsWith('<')) {
				xmlSourceString = decoded;
			}
		}

		// если это не XML (нет <), то это хеш -> подписываем через signHash
		if (xmlSourceString.indexOf('<') === -1) {
			const base64Cert = (await this.getCertInfoByThumbprint(thumbprint))
				.base64;
			const algResult = await this.getHashAlgIdBySendSoap(base64Cert);
			if (!algResult?.hashAlgId) {
				throw new Error(
					'Выбранный сертификат не подходит для подписи. Пожалуйста, выберите другой сертификат',
				);
			}
			const signResult = await this.signHash(
				xmlSourceString,
				algResult.hashAlgId,
				thumbprint,
				true,
			);
			if (!signResult?.success) {
				throw new Error(
					'Выбранный сертификат не подходит для подписи. Пожалуйста, выберите другой сертификат',
				);
			}
			return signResult;
		}

		// иначе это XML -> подписываем как XML
		return await this._signXml(thumbprint, xmlSourceString);
	};

	/** Подписываем XML документ */
	static _signXml = async (thumbprint: string, digestXml: string) => {
		const cert = await getCertificate(thumbprint);
		const algoId = await cert.getAlgorithm();
		return await this._signXmlCreate(cert, digestXml, algoId);
	};

	/** создаем XML подпись с использованием CAdESCOM.SignedXML */
	static _signXmlCreate = async (
		oCertificate: Certificate,
		digestXml: string,
		algoOid: string,
	) => {
		let signMethod = XmlDsigGost3410Url;
		let digestMethod = XmlDsigGost3411Url;

		if (algoOid === '1.2.643.7.1.1.1.1') {
			// алгоритм подписи ГОСТ Р 34.10-2012 с ключом 256 бит
			signMethod =
				'urn:ietf:params:xml:ns:cpxmlsec:algorithms:gostr34102012-gostr34112012-256';
			digestMethod =
				'urn:ietf:params:xml:ns:cpxmlsec:algorithms:gostr34112012-256';
		} else if (algoOid === '1.2.643.7.1.1.1.2') {
			// алгоритм подписи ГОСТ Р 34.10-2012 с ключом 512 бит
			signMethod =
				'urn:ietf:params:xml:ns:cpxmlsec:algorithms:gostr34102012-gostr34112012-512';
			digestMethod =
				'urn:ietf:params:xml:ns:cpxmlsec:algorithms:gostr34112012-512';
		} else if (algoOid === '1.2.643.2.2.19') {
			// алгоритм ГОСТ Р 34.10-2001
			signMethod =
				'urn:ietf:params:xml:ns:cpxmlsec:algorithms:gostr34102001-gostr3411';
			digestMethod =
				'urn:ietf:params:xml:ns:cpxmlsec:algorithms:gostr3411';
		}
		// Создаем объект CAdESCOM.CPSigner
		const oSigner =
			await window.cadesplugin.CreateObjectAsync('CAdESCOM.CPSigner');
		await oSigner.propset_Certificate(oCertificate);
		// Создаем объект CAdESCOM.SignedXML
		const oSignedXml =
			await window.cadesplugin.CreateObjectAsync('CAdESCOM.SignedXML');
		//Выставляем кодировку Base64.
		if (oSignedXml.ContentEncoding) {
			await oSignedXml.propset_ContentEncoding(CADESCOM_BASE64_TO_BINARY);
		}

		let tempdatatosign = this.tryDecodeBase64(digestXml);

		tempdatatosign = tempdatatosign.replace(
			'http://www.w3.org/2001/04/xmldsig-more#gostr34102001-gostr3411',
			signMethod,
		);
		tempdatatosign = tempdatatosign.replace(
			'http://www.w3.org/2001/04/xmldsig-more#gostr3411',
			digestMethod,
		);

		await oSignedXml.propset_Content(btoa(tempdatatosign));
		await oSignedXml.propset_SignatureType(
			CADESCOM_XML_SIGNATURE_TYPE_TEMPLATE,
		);
		await oSignedXml.propset_SignatureMethod(signMethod);
		await oSignedXml.propset_DigestMethod(digestMethod);

		const sign = await oSignedXml.Sign(oSigner);
		if (!sign || typeof sign !== 'string') {
			throw new Error(
				'Выбранный сертификат не подходит для подписи. Пожалуйста, выберите другой сертификат',
			);
		}

		const certBase64 = await this._getCertificateBase64(oCertificate);
		return { sign, cert: certBase64 };
	};

	/** создаем raw подпись хеша через CAdESCOM.RawSignature */
	static _createRawSignature = async (
		oCertificate: Certificate,
		oHashedData: unknown,
	) => {
		const oRawSignature = await window.cadesplugin.CreateObjectAsync(
			'CAdESCOM.RawSignature',
		);
		const sign = await oRawSignature.SignHash(oHashedData, oCertificate);
		if (!sign || typeof sign !== 'string') {
			throw new Error(
				'Выбранный сертификат не подходит для подписи. Пожалуйста, выберите другой сертификат',
			);
		}
		const certBase64 = await this._getCertificateBase64(oCertificate);
		return { sign, cert: certBase64 };
	};

	/**
	 * Метод создания хэш для подписи
	 * @param oCertificate
	 * @param oHashedData
	 */
	static _createSignHash = async (
		oCertificate: Certificate,
		oHashedData: string,
	): Promise<IResult<string>> => {
		try {
			const oSignedData = await window.cadesplugin.CreateObjectAsync(
				'CAdESCOM.CadesSignedData',
			);
			const oSigner =
				await window.cadesplugin.CreateObjectAsync('CAdESCOM.CPSigner');
			await oSigner.propset_Certificate(oCertificate);

			const data = await oSignedData.SignHash(
				oHashedData,
				oSigner,
				CADESCOM_CADES_BES,
			);
			return { data, success: true, message: '' };
		} catch (err) {
			const error = err as Error;
			return { success: false, message: error.message };
		}
	};

	/**
	 * Метод инициализации объекта CAdESCOM.HashedData
	 * @param hashAlg
	 * @param sHashValue
	 */
	static _initializeHashedData = async (
		hashAlg: number,
		sHashValue: string,
	): Promise<IResult<string>> => {
		try {
			const oHashedData = await window.cadesplugin.CreateObjectAsync(
				'CAdESCOM.HashedData',
			);
			oHashedData.propset_Algorithm(hashAlg);
			oHashedData.SetHashValue(sHashValue);

			const data = await oHashedData;
			return { data, success: true, message: '' };
		} catch (e) {
			const error = e as Error;
			return { success: false, message: error.message };
		}
	};

	/** экспортируем сертификат в base64 (работает с crypto-pro-js и cadesplugin) */
	static _getCertificateBase64 = async (
		oCertificate: unknown,
	): Promise<string> => {
		if (!oCertificate) {
			return '';
		}
		const cert = oCertificate as {
			exportBase64?: () => Promise<string>;
			Export?: (format: number) => Promise<unknown>;
		};

		if (typeof cert.exportBase64 === 'function') {
			return await cert.exportBase64();
		}
		if (typeof cert.Export === 'function') {
			const result = await cert.Export(0);
			return typeof result === 'string' ? result : '';
		}
		return '';
	};

	/**
	 * Метод получения сертификата по отпечатку
	 * @param thumbprint отпечаток
	 */
	static async _getCertificate(
		thumbprint: string,
	): Promise<IResult<Certificate>> {
		try {
			const oStore =
				await window.cadesplugin.CreateObjectAsync('CAdESCOM.Store');
			await oStore.Open(
				CAPICOM_CURRENT_USER_STORE,
				CAPICOM_MY_STORE,
				CAPICOM_STORE_OPEN_MAXIMUM_ALLOWED,
			);

			const oCertificates = await oStore.Certificates;
			const certs = await oCertificates.Find(
				CAPICOM_CERTIFICATE_FIND_SHA1_HASH,
				thumbprint,
			);

			const count = await certs.Count;
			if (!count) {
				oStore.Close();
				return {
					success: false,
					message:
						'Не удалось найти сертификата по отпечатку: ' +
						thumbprint +
						'Пустой список.',
				};
			}

			const cert = await certs.Item(1);
			oStore.Close();

			const data = await cert;
			return { data, success: true, message: '' };
		} catch (e) {
			const error = e as Error;
			return { success: false, message: error.message };
		}
	}
}

export default APICertificate;
